import React from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Badge,
  Button,
  UncontrolledTooltip,
} from "reactstrap";
import VisibilityIcon from "@mui/icons-material/Visibility";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";
import { useNavigate } from "react-router-dom";

const getAuthorName = (userObj) => {
  if (!userObj) return "";
  if (typeof userObj === "string") {
    const trimmed = userObj.trim();
    if (!trimmed || /^[0-9a-fA-F]{24}$/.test(trimmed)) return "";
    return trimmed;
  }
  const name = `${userObj.firstName || ""} ${userObj.lastName || ""}`.trim();
  return name || userObj.name || userObj.email || "";
};

const SupportDetailsCard = ({ ticket }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!ticket) return null;

  const statusKey = ticket.status || "unknown";
  const statusColor = helpers.getStatusClass(statusKey, "ticket").replace("status-", "");
  const formattedStatus = t(`page.support-tickets.status.${statusKey}`, statusKey.replace(/_/g, " "));

  const resolutionAuthor =
    getAuthorName(ticket.resolvedBy) ||
    getAuthorName(ticket.updatedBy) ||
    getAuthorName(ticket.assignedTo) ||
    getAuthorName(ticket.user) ||
    getAuthorName(ticket.userId);

  const reopenAuthor =
    getAuthorName(ticket.reopenedBy) ||
    getAuthorName(ticket.updatedBy) ||
    getAuthorName(ticket.assignedTo) ||
    getAuthorName(ticket.user) ||
    getAuthorName(ticket.userId);

  return (
    <Card className="shadow-md">
      <CardBody>
        <Row className="mb-3 align-items-center">
          <Col>
            <span className="text-muted">
              {t("page.messages.supportTicketLabel", "Support Ticket")}
            </span>
          </Col>
          <Button
            id={`view-ticket-${ticket._id}`}
            size="sm"
            color="link"
            className="mr-3 btn-secondary"
            onClick={() => navigate(`/admin/support-tickets/${ticket._id}/details?source=SupportRequest`)}
          >
            <VisibilityIcon fontSize="small" />
          </Button>
          <UncontrolledTooltip target={`view-ticket-${ticket._id}`}>
            {t("page.messages.view", "View Details")}
          </UncontrolledTooltip>
        </Row>

        <Row className="mb-2">
          <Col>
            <h5>{ticket.title}</h5>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <Badge color={statusColor} pill className="text-capitalize">
              {formattedStatus}
            </Badge>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <small className="text-muted mb-1 d-block">{t("page.messages.issue", "Issue Description")}</small>
              <div>
                {ticket.description}
              </div>
          </Col>
        </Row>

        {ticket.status !== "resolved" && ticket.reopenReason && (
          <Row className="mb-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">{t("page.support-tickets.reopenReason", "Reopen Reason")}</small>
                {reopenAuthor && <small className="text-muted font-weight-bold">{t("page.messages.by", "by")} {reopenAuthor}</small>}
              </div>
              <div className="p-2 rounded border text-dark text-sm">
                {ticket.reopenReason}
              </div>
            </Col>
          </Row>
        )}

        {ticket.status === "resolved" && ticket.resolutionNotes && (
          <Row className="mb-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">{t("page.support-tickets.resolutionNotes", "Resolution Notes")}</small>
                {resolutionAuthor && <small className="text-muted font-weight-bold">{t("page.messages.by", "by")} {resolutionAuthor}</small>}
              </div>
              <div className="p-2 rounded border text-dark text-sm">
                {ticket.resolutionNotes}
              </div>
            </Col>
          </Row>
        )}

        {ticket.user && (
          <Row className="mb-3">
            <Col>
              <small className="text-muted mb-1">{t("page.messages.support.createdBy", "Created By")}</small>
              <span className="d-block">
                {ticket.user.firstName} {ticket.user.lastName}
              </span>
              {ticket.user.email && (
                  <small className="text-muted">
                    {ticket.user.email}
                  </small>
              )}
            </Col>
          </Row>
        )}

        {ticket.assignedTo && (
          <Row>
            <Col>
              <small className="text-muted mb-1">{t("page.messages.support.assignedTo", "Assigned To")}</small>
              <span className="d-block">
                {ticket.assignedTo.firstName} {ticket.assignedTo.lastName}
              </span>
              {ticket.assignedTo.email && (
                  <small className="text-muted">
                    {ticket.assignedTo.email}
                  </small>
              )}
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
};

export default SupportDetailsCard;